import { LOGIN_SITE } from '~/lib/constants/constants'
import type {
  ReservationGivenPointsType,
  ReservationGivenPointsUsage,
  ReserveChildMenuInput,
  ReserveInfoViewerFragment,
  ReservePaymentType,
} from '~/lib/graphql/graphql'
import type { ChildMenuId, DateText, HourMinute } from '~/lib/graphql/scalars'
import { isUpToGradeSchooler } from '~/lib/seat/seat'
import { disabledGivenPointsTypePayPay, submitCardDisabled } from '../lib/checkDisabled'
import type { ReserveInputSeat } from './state'
import type {
  ReservationInputContext,
  ReservationInputEvent,
  SelectedCard,
  UpdatedEvent,
  UserType,
} from './statemachine'

export function toMemberUpdatedContext(ctx: ReservationInputContext, event: UpdatedEvent) {
  const seatId = validateSeatId(ctx, event)
  const { childMenus, childCount } = validateChildMenu(ctx, event)
  const givenPointsType = validateMemberGivenPointsType(ctx, event)
  const ikyuPointUsage = validateIkyuPointUsage(ctx, event)
  const paypayPointUsage = validatePayPayPointUsage(ctx, event)
  const card = validateCard(ctx, event)
  const appliedDiscountCoupon = validateAppliedDiscountCoupon(ctx, event)
  return {
    ...ctx,
    seatId,
    childMenus,
    childCount,
    optionMenus: event.optionMenus ?? ctx.optionMenus,
    reserverNameKana: event.reserverNameKana ?? ctx.reserverNameKana,
    reserverPhoneNumber: event.reserverPhoneNumber ?? ctx.reserverPhoneNumber,
    email: event.email ?? ctx.email,
    isReserverDifferentFromVisitor:
      event.isReserverDifferentFromVisitor ?? ctx.isReserverDifferentFromVisitor,
    visitorNameKana: event.visitorNameKana ?? ctx.visitorNameKana,
    visitorPhoneNumber: event.visitorPhoneNumber ?? ctx.visitorPhoneNumber,
    affiliateSiteFormAnswer: event.affiliateSiteFormAnswer ?? ctx.affiliateSiteFormAnswer,
    requestsAndComments: event.requestsAndComments ?? ctx.requestsAndComments,
    subscribeMailMagazineIkyuNews:
      event.subscribeMailMagazineIkyuNews ?? ctx.subscribeMailMagazineIkyuNews,
    subscribeMailMagazinePointCoupon:
      event.subscribeMailMagazinePointCoupon ?? ctx.subscribeMailMagazinePointCoupon,
    agreedToReserveRequest: event.agreedToReserveRequest ?? ctx.agreedToReserveRequest,
    // TODO: 支払い方法変更のバリデーションする
    paymentMethod: event.paymentMethod ?? ctx.paymentMethod,
    discountCouponType: event.discountCouponType ?? ctx.discountCouponType,
    isIkyuPointEnabled: event.isIkyuPointEnabled ?? ctx.isIkyuPointEnabled,
    isPayPayPointEnabled: event.isPayPayPointEnabled ?? ctx.isPayPayPointEnabled,
    isAllIkyuPointSelected: event.isAllIkyuPointSelected ?? ctx.isAllIkyuPointSelected,
    isAllPayPayPointSelected: event.isAllPayPayPointSelected ?? ctx.isAllPayPayPointSelected,
    isDiscountCouponEnabled: event.isDiscountCouponEnabled ?? ctx.isDiscountCouponEnabled,
    card,
    givenPointsType,
    givenPointsUsage: event.givenPointsUsage ?? ctx.givenPointsUsage,
    ikyuPointUsage,
    paypayPointUsage,
    appliedDiscountCoupon,
  }
}

export function toPreMemberUpdatedContext(ctx: ReservationInputContext, event: UpdatedEvent) {
  const seatId = validateSeatId(ctx, event)
  const { childMenus, childCount } = validateChildMenu(ctx, event)
  const password = validatePassword(ctx, event)
  const card = validateCard(ctx, event)
  const appliedDiscountCoupon = validateAppliedDiscountCoupon(ctx, event)
  return {
    ...ctx,
    seatId,
    childMenus,
    childCount,
    optionMenus: event.optionMenus ?? ctx.optionMenus,
    reserverNameKana: event.reserverNameKana ?? ctx.reserverNameKana,
    reserverPhoneNumber: event.reserverPhoneNumber ?? ctx.reserverPhoneNumber,
    email: event.email ?? ctx.email,
    affiliateSiteFormAnswer: event.affiliateSiteFormAnswer ?? ctx.affiliateSiteFormAnswer,
    requestsAndComments: event.requestsAndComments ?? ctx.requestsAndComments,
    // TODO: 支払い方法変更のバリデーションする
    paymentMethod: event.paymentMethod ?? ctx.paymentMethod,
    discountCouponType: event.discountCouponType ?? ctx.discountCouponType,
    appliedDiscountCoupon,
    isDiscountCouponEnabled: event.isDiscountCouponEnabled ?? ctx.isDiscountCouponEnabled,
    card,
    givenPointsType: 'IKYU' as ReservationGivenPointsType,
    givenPointsUsage: event.givenPointsUsage ?? ctx.givenPointsUsage,
    paypayPointUsage: 0,
    password,
    agreedToReserveRequest: event.agreedToReserveRequest ?? ctx.agreedToReserveRequest,
  }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('toPreMemberUpdatedContext', async () => {
    expect(
      toPreMemberUpdatedContext(
        {
          givenPointsType: 'IKYU',
          givenPointsUsage: 'USE_INSTANTLY',
          paypayPointUsage: 0,
        } as ReservationInputContext,
        {
          givenPointsType: 'PAYPAY',
          reserverNameKana: 'いっきゅうたろう',
          userStatus: {
            givenPoints: 100,
          },
        } as UpdatedEvent
      )
    ).toEqual({
      givenPointsType: 'IKYU',
      givenPointsUsage: 'USE_INSTANTLY',
      paypayPointUsage: 0,
      reserverNameKana: 'いっきゅうたろう',
    })
    expect(
      toPreMemberUpdatedContext(
        {
          givenPointsType: 'IKYU',
          givenPointsUsage: 'USE_INSTANTLY',
          paypayPointUsage: 0,
        } as ReservationInputContext,
        {
          givenPointsType: 'IKYU',
          userStatus: {
            givenPoints: 100,
          },
        } as UpdatedEvent
      )
    ).toEqual({
      givenPointsType: 'IKYU',
      givenPointsUsage: 'USE_INSTANTLY',
      paypayPointUsage: 0,
    })
  })
}

export function toGuestUpdatedContext(ctx: ReservationInputContext, event: UpdatedEvent) {
  const seatId = validateSeatId(ctx, event)
  const { childMenus, childCount } = validateChildMenu(ctx, event)
  const password = validatePassword(ctx, event)
  const card = validateCard(ctx, event)
  return {
    ...ctx,
    seatId,
    childMenus,
    childCount,
    optionMenus: event.optionMenus ?? ctx.optionMenus,
    reserverNameKana: event.reserverNameKana ?? ctx.reserverNameKana,
    reserverPhoneNumber: event.reserverPhoneNumber ?? ctx.reserverPhoneNumber,
    email: event.email ?? ctx.email,
    affiliateSiteFormAnswer: event.affiliateSiteFormAnswer ?? ctx.affiliateSiteFormAnswer,
    requestsAndComments: event.requestsAndComments ?? ctx.requestsAndComments,
    // ゲストは常に固定
    paymentMethod: 'ONSITE' as ReservePaymentType,
    givenPointsType: 'IKYU' as ReservationGivenPointsType,
    givenPointsUsage: 'SAVE' as ReservationGivenPointsUsage,
    paypayPointUsage: 0,
    password,
    card,
    agreedToReserveRequest: event.agreedToReserveRequest ?? ctx.agreedToReserveRequest,
  }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('toGuestUpdatedContext', async () => {
    expect(
      toGuestUpdatedContext(
        {
          paymentMethod: 'ONLINE',
          givenPointsType: 'PAYPAY',
          givenPointsUsage: 'USE_INSTANTLY',
          paypayPointUsage: 1000,
        } as ReservationInputContext,
        {
          type: 'updated',
          reserverNameKana: 'いっきゅうたろう',
          password: 'password',
        } as UpdatedEvent
      )
    ).toEqual({
      paymentMethod: 'ONSITE',
      givenPointsType: 'IKYU',
      givenPointsUsage: 'SAVE',
      paypayPointUsage: 0,
      reserverNameKana: 'いっきゅうたろう',
      password: 'password',
    })
  })
}

export function toCardUpdatedContext(
  ctx: ReservationInputContext,
  event: Extract<ReservationInputEvent, { type: 'registeredCard' }>
) {
  const card = validateCard(ctx, event)
  return {
    ...ctx,
    card,
  }
}

function validateSeatId(ctx: ReservationInputContext, event: UpdatedEvent) {
  if (event.seatId === undefined || event.userStatus === undefined) {
    return ctx.seatId
  }
  const selected = event.userStatus?.seats?.find((s) => s.seatId === event.seatId)
  if (selected === undefined) {
    return ctx.seatId
  }
  const childCount = event.userStatus?.childCount ?? 0
  if (!isSeatAvailable(selected, { childCount })) {
    return ctx.seatId
  }
  return event.seatId
}

// TODO: 循環参照になってしまっているので assign で使用する関数は全て
//       assign 内で実装するようにして別ファイルへの依存をなくす
/**
 * 席が利用できるかどうかを判断する
 *
 * 現時点の条件は子供同伴かどうかのみ
 * 子供がいない場合、または子供利用可能な席の場合は予約可能な席とみなす
 */
export function isSeatAvailable(seat: ReserveInputSeat, conditions: { childCount: number }) {
  return conditions.childCount === 0 || isUpToGradeSchooler(seat.childUse)
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('isSeatAvailable', () => {
    it('未就学児以上が利用可能な席', () => {
      const seat = { childUse: 'PRE_SCHOOLERS' } as ReserveInputSeat
      expect(isSeatAvailable(seat, { childCount: 0 })).toBe(true)
      expect(isSeatAvailable(seat, { childCount: 1 })).toBe(true)
    })
    it('小学生以上が利用可能な席', () => {
      const seat = { childUse: 'GRADE_SCHOOLERS' } as ReserveInputSeat
      expect(isSeatAvailable(seat, { childCount: 0 })).toBe(true)
      expect(isSeatAvailable(seat, { childCount: 1 })).toBe(true)
    })
    it('中学生以上が利用可能な席', () => {
      const seat = { childUse: 'OVER_MIDDLE_SCHOOLERS' } as ReserveInputSeat
      expect(isSeatAvailable(seat, { childCount: 0 })).toBe(true)
      expect(isSeatAvailable(seat, { childCount: 1 })).toBe(false)
    })
  })
}

function validateChildMenu(
  ctx: ReservationInputContext,
  event: Pick<UpdatedEvent, 'childMenus' | 'childCount'>
) {
  if (event.childMenus === undefined || event.childCount === undefined) {
    return { childMenus: ctx.childMenus, childCount: ctx.childCount }
  }
  if (disabledChildMenu(event.childCount, event.childMenus)) {
    return { childMenus: ctx.childMenus, childCount: ctx.childCount }
  }
  return { childMenus: event.childMenus, childCount: event.childCount }
}

// TODO: 循環参照になってしまっているので assign で使用する関数は全て
//       assign 内で実装するようにして別ファイルへの依存をなくす
export function disabledChildMenu(childCount: number, selected: ReserveChildMenuInput[]) {
  const total = selected.reduce((acc, cur) => acc + cur.count, 0)
  return childCount < total
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('disabledChildMenu', () => {
    expect(disabledChildMenu(0, [])).toBe(false)
    expect(
      disabledChildMenu(1, [
        { childMenuId: '1' as ChildMenuId, count: 1 },
      ] as ReserveChildMenuInput[])
    ).toBe(false)
    expect(
      disabledChildMenu(1, [
        { childMenuId: '1' as ChildMenuId, count: 1 },
        { childMenuId: '2' as ChildMenuId, count: 1 },
      ] as ReserveChildMenuInput[])
    ).toBe(true)
  })
}

export function validateMemberGivenPointsType(
  ctx: ReservationInputContext,
  event: Pick<UpdatedEvent, 'givenPointsType' | 'userStatus'>
) {
  const status = event.userStatus
  const disabledAll = (status?.givenPoints ?? 0) === 0
  if (disabledAll || event.givenPointsType === undefined) {
    return ctx.givenPointsType
  }
  //　YID ログインの場合は獲得ポイントを即時利用する場合でも
  // 必ず PointType は PAYPAY になる
  if (status?.viewer?.loginSite === LOGIN_SITE.Yahoo) {
    return 'PAYPAY'
  }
  // 一休ログインで即時利用の場合
  // 必ず PointType は IKYU になる
  if (ctx.givenPointsUsage === 'USE_INSTANTLY' || disabledGivenPointsTypePayPay(status?.cause)) {
    return 'IKYU'
  }
  return event.givenPointsType
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('validateMemberGivenPointsType', () => {
    it('獲得ポイントが0で全て無効になっている', () => {
      expect(
        validateMemberGivenPointsType({ givenPointsType: 'IKYU' } as ReservationInputContext, {
          givenPointsType: 'PAYPAY',
          userStatus: {
            givenPoints: 0,
          },
        })
      ).toBe('IKYU')
    })
    it('YIDログイン', () => {
      expect(
        validateMemberGivenPointsType({ givenPointsType: 'PAYPAY' } as ReservationInputContext, {
          givenPointsType: 'IKYU',
          userStatus: {
            givenPoints: 100,
            viewer: {
              loginSite: LOGIN_SITE.Yahoo,
            } as ReserveInfoViewerFragment,
          },
        })
      ).toBe('PAYPAY')
    })
    it('一休ログインで即時利用', () => {
      expect(
        validateMemberGivenPointsType(
          { givenPointsType: 'IKYU', givenPointsUsage: 'USE_INSTANTLY' } as ReservationInputContext,
          {
            givenPointsType: 'PAYPAY',
            userStatus: {
              givenPoints: 100,
              viewer: {
                loginSite: LOGIN_SITE.Ikyu,
              } as ReserveInfoViewerFragment,
            },
          }
        )
      ).toBe('IKYU')
    })
    it('PayPayポイントが貯めることができない', () => {
      expect(
        validateMemberGivenPointsType(
          { givenPointsType: 'IKYU', givenPointsUsage: 'SAVE' } as ReservationInputContext,
          {
            givenPointsType: 'PAYPAY',
            userStatus: {
              givenPoints: 100,
              viewer: {
                loginSite: LOGIN_SITE.Ikyu,
              } as ReserveInfoViewerFragment,
              cause: 'YAHOO_ID_NOT_CONNECTED',
            },
          }
        )
      ).toBe('IKYU')
    })
  })
}

function validateIkyuPointUsage(
  ctx: ReservationInputContext,
  event: Pick<UpdatedEvent, 'ikyuPointUsage' | 'userStatus'>
) {
  const v = event.userStatus
  if (v === undefined) {
    return ctx.ikyuPointUsage
  }
  // ゲストの場合は一休ポイントを利用できない
  if (!v.isMember) {
    return 0
  }
  if (v.availableMaxIkyuPoint === undefined || event.ikyuPointUsage === undefined) {
    return ctx.ikyuPointUsage
  }
  // 利用可能な一休ポイントの最大値を超えてはいけない
  if (v.availableMaxIkyuPoint < event.ikyuPointUsage) {
    return v.availableMaxIkyuPoint
  }
  return event.ikyuPointUsage
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  const ctx = {
    ikyuPointUsage: 100,
  } as ReservationInputContext
  describe('validateIkyuPointUsage', () => {
    it('ゲスト', () => {
      const event = {
        ikyuPointUsage: 1000,
        userStatus: { isMember: false, availableMaxIkyuPoint: 1000 },
      }
      expect(validateIkyuPointUsage(ctx, event)).toBe(0)
    })
    it('userStatus が event に含まれない', () => {
      const event = {
        ikyuPointUsage: 1000,
      }
      expect(validateIkyuPointUsage(ctx, event)).toBe(ctx.ikyuPointUsage)
    })
    it('userStatus.availableMaxIkyuPoint が event に含まれない', () => {
      const event = {
        ikyuPointUsage: 1000,
        userStatus: { isMember: true },
      }
      expect(validateIkyuPointUsage(ctx, event)).toBe(ctx.ikyuPointUsage)
    })
    it('ikyuPointUsage が event に含まれない', () => {
      const event = {
        userStatus: { isMember: true, availableMaxIkyuPoint: 1000 },
      }
      expect(validateIkyuPointUsage(ctx, event)).toBe(ctx.ikyuPointUsage)
    })
    it('入力成功 利用可能な一休ポイントの最大値を超えている場合は、最大値までの利用に制限される', () => {
      const event = {
        ikyuPointUsage: 2000,
        userStatus: { isMember: true, availableMaxIkyuPoint: 1000 },
      }
      expect(validateIkyuPointUsage(ctx, event)).toBe(1000)
    })
    it('入力成功 入力した一休ポイントが返る', () => {
      const event = {
        ikyuPointUsage: 500,
        userStatus: { isMember: true, availableMaxIkyuPoint: 1000 },
      }
      expect(validateIkyuPointUsage(ctx, event)).toBe(500)
    })
  })
}

export function validatePayPayPointUsage(
  ctx: ReservationInputContext,
  event: Pick<UpdatedEvent, 'paypayPointUsage' | 'userStatus'>
) {
  const v = event.userStatus
  if (v === undefined) {
    return ctx.paypayPointUsage
  }
  // ゲストの場合は PayPay 残高利用を利用できない
  if (!v.isMember) {
    return 0
  }
  // PayPay 残高利用不可店舗の場合は PayPay 残高利用を利用できない
  if (!v.restaurant?.paypayAvailable) {
    return 0
  }
  if (v.availableMaxPayPayBalance === undefined || event.paypayPointUsage === undefined) {
    return ctx.paypayPointUsage
  }
  // 利用可能な PayPay 残高利用の最大値を超えてはいけない
  if (v.availableMaxPayPayBalance < event.paypayPointUsage) {
    return v.availableMaxPayPayBalance
  }
  return event.paypayPointUsage
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('validatePayPayPointUsage', () => {
    it('ゲスト', () => {
      expect(
        validatePayPayPointUsage(
          {
            paypayPointUsage: 1000,
          } as ReservationInputContext,
          {
            paypayPointUsage: 1000,
            userStatus: {
              isMember: false,
              restaurant: {
                paypayAvailable: true,
              },
              availableMaxPayPayBalance: 1000,
            },
          } as UpdatedEvent
        )
      ).toBe(0)
    })
    it('PayPay 残高利用不可店舗', () => {
      expect(
        validatePayPayPointUsage(
          {
            paypayPointUsage: 1000,
          } as ReservationInputContext,
          {
            paypayPointUsage: 1000,
            userStatus: {
              isMember: true,
              restaurant: {
                paypayAvailable: false,
              },
              availableMaxPayPayBalance: 999,
            },
          } as UpdatedEvent
        )
      ).toBe(0)
    })
    it('event に paypayPointUsage を入れていない', () => {
      expect(
        validatePayPayPointUsage(
          {
            paypayPointUsage: 100,
          } as ReservationInputContext,
          {
            givenPointsType: 'PAYPAY',
            userStatus: {
              isMember: true,
              restaurant: {
                paypayAvailable: true,
              },
              availableMaxPayPayBalance: 999,
            },
          } as UpdatedEvent
        )
      ).toBe(100)
    })
    it('利用可能な PayPay 残高の最大値を超えている', () => {
      expect(
        validatePayPayPointUsage(
          {
            paypayPointUsage: 1000,
          } as ReservationInputContext,
          {
            paypayPointUsage: 1001,
            userStatus: {
              isMember: true,
              restaurant: {
                paypayAvailable: true,
              },
              availableMaxPayPayBalance: 1000,
            },
          } as UpdatedEvent
        )
      ).toBe(1000)
    })
    it('入力成功', () => {
      expect(
        validatePayPayPointUsage(
          {
            paypayPointUsage: 500,
          } as ReservationInputContext,
          {
            paypayPointUsage: 1000,
            userStatus: {
              isMember: true,
              restaurant: {
                paypayAvailable: true,
              },
              availableMaxPayPayBalance: 1500,
            },
          } as UpdatedEvent
        )
      ).toBe(1000)
    })
  })
}

export function toNewUserStatus(
  ctx: ReservationInputContext,
  userType?: UserType,
  isTrusted?: boolean
): ReservationInputContext {
  return {
    ...ctx,
    userType,
    isTrusted,
  }
}

export function validatePassword(
  ctx: ReservationInputContext,
  event: Pick<UpdatedEvent, 'password'>
) {
  if (event.password === undefined) {
    return ctx.password
  }
  return event.password
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('validatePassword', async () => {
    const ctx = {
      password: 'passwor',
    } as ReservationInputContext
    expect(
      validatePassword(ctx, {
        password: 'password',
      } as UpdatedEvent)
    ).toBe('password')
    expect(validatePassword(ctx, {})).toBe('passwor')
  })
}

export function setAuthResult(
  ctx: ReservationInputContext,
  event: Extract<ReservationInputEvent, { type: 'authenticated' }>
): ReservationInputContext {
  return {
    ...ctx,
    authResult: event.result,
  }
}

function validateCard(
  ctx: ReservationInputContext,
  event: Pick<UpdatedEvent, 'card' | 'userStatus'>
) {
  const v = event.userStatus
  // すべてのカードが削除された場合はカード選択状態をクリアするために undefined を返す
  if (v?.card?.selected?.type === 'deleted') {
    return undefined
  }
  if (event.card === undefined) {
    return ctx.card
  }
  // 既存カードを選択している場合は入力フォームのバリデーションが不要なので先に返す
  if (event.card.type === 'registered') {
    return event.card
  }
  if (v === undefined || v.card === undefined) {
    return ctx.card
  }
  if (submitCardDisabled(v.card.isInputCompleted, v.card.errors, v.card.selected)) {
    return ctx.card
  }
  return event.card
}
if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest
  describe('validateCard', async () => {
    const ctx = {} as ReservationInputContext
    const card = {
      type: 'new',
      newCard: {
        maskedNumber: '1234',
        goodThru: '12/34',
        holderName: '123',
        brand: 'VISA',
        token: 'token',
      },
    } as SelectedCard
    it('deleted', () => {
      expect(
        validateCard(ctx, {
          userStatus: {
            card: {
              isInputCompleted: false,
              errors: {},
              selected: { type: 'deleted' },
            },
          },
        })
      ).toBe(undefined)
    })
    it('undefined', () => {
      expect(validateCard(ctx, {})).toBe(undefined)
    })
    it('エラーが表示されている', () => {
      expect(
        validateCard(ctx, {
          card,
          userStatus: {
            card: {
              isInputCompleted: true,
              errors: {
                cardNumberError: 'error',
                cardHolderError: 'error',
                cardExpirationError: 'error',
                securityCodeError: 'error',
              },
              selected: { type: 'new' },
            },
          },
        })
      ).toBe(undefined)
    })
    it('updated 成功', () => {
      expect(
        validateCard(ctx, {
          card,
          userStatus: {
            card: {
              isInputCompleted: true,
              errors: {},
              selected: { type: 'new' },
            },
          },
        })
      ).toBe(card)
    })
  })
}

export function toNewReserveCondition(
  ctx: ReservationInputContext,
  event: Extract<ReservationInputEvent, { type: 'reserveConditionChanged' }>
): ReservationInputContext {
  const { type, ...payload } = event
  // ../specification.md 「総人数変更における大人と子供の人数について」参照
  return payload.visitorsCount > ctx.childCount
    ? {
        ...ctx,
        ...payload,
      }
    : {
        ...ctx,
        ...payload,
        childCount: 0,
        childMenus: [],
      }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  const ctx = {
    visitorsCount: 4,
    visitDate: '2024-03-28' as DateText,
    visitTime: '12:00' as HourMinute,
    childCount: 2,
    childMenus: [{ childMenuId: '1', count: 1 }] as ReserveChildMenuInput[],
  } as ReservationInputContext
  const event = {
    type: 'reserveConditionChanged',
    visitorsCount: 0,
    visitDate: '2024-03-29' as DateText,
    visitTime: '13:00' as HourMinute,
  } satisfies Extract<ReservationInputEvent, { type: 'reserveConditionChanged' }>
  it('総人数が子供人数より多い場合は、人数日時のみ変更する', () => {
    event.visitorsCount = 3
    expect(toNewReserveCondition(ctx, event)).toEqual({
      visitorsCount: 3,
      visitDate: '2024-03-29',
      visitTime: '13:00',
      childCount: 2,
      childMenus: [{ childMenuId: '1', count: 1 }] as ReserveChildMenuInput[],
    })
  })
  it('総人数が子供人数以下の場合は、人数日時の変更とともに子供人数とお子様メニューを初期化する', () => {
    event.visitorsCount = 2
    expect(toNewReserveCondition(ctx, event)).toEqual({
      visitorsCount: 2,
      visitDate: '2024-03-29',
      visitTime: '13:00',
      childCount: 0,
      childMenus: [],
    })
    event.visitorsCount = 1
    expect(toNewReserveCondition(ctx, event)).toEqual({
      visitorsCount: 1,
      visitDate: '2024-03-29',
      visitTime: '13:00',
      childCount: 0,
      childMenus: [],
    })
  })
}

function validateAppliedDiscountCoupon(
  ctx: ReservationInputContext,
  event: Pick<UpdatedEvent, 'appliedDiscountCoupon'>
) {
  return event.appliedDiscountCoupon === undefined
    ? ctx.appliedDiscountCoupon
    : event.appliedDiscountCoupon
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('validateAppliedDiscountCoupon', async () => {
    const ctx = {
      appliedDiscountCoupon: { code: 'coupon' },
    } as ReservationInputContext
    it('イベントなし', () => {
      expect(validateAppliedDiscountCoupon(ctx, {})).toEqual({ code: 'coupon' })
    })
    it('クーポン適用', () => {
      expect(
        validateAppliedDiscountCoupon(ctx, {
          appliedDiscountCoupon: { code: 'coupon2' },
        } as UpdatedEvent)
      ).toEqual({ code: 'coupon2' })
    })
    it('クーポン解除', () => {
      expect(validateAppliedDiscountCoupon(ctx, { appliedDiscountCoupon: null })).toEqual(null)
    })
  })
}
